import { isValidElement, useEffect, useRef } from 'react';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { selectChangeStripData, useReportStore } from '@/entities/report';
import type { DeviceEventPreviewStripProps } from './deviceEventPreviewStrip';

/**
 * Supplies a device event thumbnail with its pending change and its click behaviour. Looks the event up in the report store's change list, and clears the tab's stored timeout when the active sub-tab changes or the thumbnail unmounts.
 *
 * @param props - Thumbnail props with the tab store key, active sub-tab, the event, its index, overlay content and the select callback.
 * @returns An object carrying the matching pending change and the thumbnail click handler.
 */
const useDeviceEventPreviewStrip = ({
  keyRecoil,
  activeButton,
  event,
  overlayContent,
  index,
  onClickSelectEvent,
}: Pick<
  DeviceEventPreviewStripProps,
  'keyRecoil' | 'activeButton' | 'event' | 'overlayContent' | 'index' | 'onClickSelectEvent'
>) => {
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const eleChange = changeStripData.find((x) => x.id === event?.id);
  const timeoutRef = useRef<any>();
  /**
   * Selects the previewed device event unless the click is a modifier or overlay interaction. Swallows the event when overlay content is rendered or shift, ctrl or meta is held, otherwise forwards the event and its index to the parent select handler.
   *
   * @param e - Mouse event from the strip thumbnail.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    // Prevent click to select new event,shift or ctrl when select multiple
    if (e && (isValidElement(overlayContent ?? null) || e?.shiftKey || e?.ctrlKey || e?.metaKey)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    onClickSelectEvent(event, index);
  };

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
    },
    [activeButton],
  );

  return {
    eleChange,
    onClick,
  };
};

export default useDeviceEventPreviewStrip;
